import type { CytoscapeElement } from '../types/graph';

export interface GraphScore {
    nodeId: string;
    score: number;
    clusterId?: number;
}

interface GraphIndex {
    nodes: string[];
    outgoing: Map<string, Set<string>>;
    incoming: Map<string, Set<string>>;
    undirected: Map<string, Set<string>>;
}

function buildIndex(elements: CytoscapeElement[]): GraphIndex {
    const nodes = elements
        .filter(element => !element.data?.source && !!element.data?.id)
        .map(element => String(element.data.id))
        .sort();
    const nodeSet = new Set(nodes);
    const outgoing = new Map(nodes.map(node => [node, new Set<string>()]));
    const incoming = new Map(nodes.map(node => [node, new Set<string>()]));
    const undirected = new Map(nodes.map(node => [node, new Set<string>()]));

    for (const element of elements) {
        const source = element.data?.source ? String(element.data.source) : '';
        const target = element.data?.target ? String(element.data.target) : '';
        if (!nodeSet.has(source) || !nodeSet.has(target) || source === target) continue;
        outgoing.get(source)?.add(target);
        incoming.get(target)?.add(source);
        undirected.get(source)?.add(target);
        undirected.get(target)?.add(source);
    }
    return { nodes, outgoing, incoming, undirected };
}

export function calculatePageRank(elements: CytoscapeElement[]): GraphScore[] {
    const { nodes, outgoing, incoming } = buildIndex(elements);
    if (nodes.length === 0) return [];
    const damping = 0.85;
    const base = (1 - damping) / nodes.length;
    let ranks = new Map(nodes.map(node => [node, 1 / nodes.length]));

    for (let iteration = 0; iteration < 100; iteration += 1) {
        const dangling = nodes
            .filter(node => (outgoing.get(node)?.size || 0) === 0)
            .reduce((sum, node) => sum + (ranks.get(node) || 0), 0);
        const next = new Map<string, number>();
        let delta = 0;
        for (const node of nodes) {
            let inbound = 0;
            for (const source of incoming.get(node) || []) {
                inbound += (ranks.get(source) || 0) / (outgoing.get(source)?.size || 1);
            }
            const score = base + damping * (inbound + dangling / nodes.length);
            next.set(node, score);
            delta += Math.abs(score - (ranks.get(node) || 0));
        }
        ranks = next;
        if (delta < 1e-10) break;
    }

    const max = Math.max(...ranks.values(), 1);
    return nodes.map(node => ({ nodeId: node, score: (ranks.get(node) || 0) / max }));
}

export function calculateBetweenness(elements: CytoscapeElement[]): GraphScore[] {
    const { nodes, undirected } = buildIndex(elements);
    const centrality = new Map(nodes.map(node => [node, 0]));

    for (const source of nodes) {
        const stack: string[] = [];
        const predecessors = new Map(nodes.map(node => [node, [] as string[]]));
        const paths = new Map(nodes.map(node => [node, 0]));
        const distance = new Map(nodes.map(node => [node, -1]));
        paths.set(source, 1);
        distance.set(source, 0);
        const queue = [source];

        while (queue.length) {
            const vertex = queue.shift()!;
            stack.push(vertex);
            for (const neighbour of undirected.get(vertex) || []) {
                if ((distance.get(neighbour) ?? -1) < 0) {
                    queue.push(neighbour);
                    distance.set(neighbour, (distance.get(vertex) || 0) + 1);
                }
                if (distance.get(neighbour) === (distance.get(vertex) || 0) + 1) {
                    paths.set(neighbour, (paths.get(neighbour) || 0) + (paths.get(vertex) || 0));
                    predecessors.get(neighbour)?.push(vertex);
                }
            }
        }

        const dependency = new Map(nodes.map(node => [node, 0]));
        while (stack.length) {
            const vertex = stack.pop()!;
            for (const predecessor of predecessors.get(vertex) || []) {
                const vertexPaths = paths.get(vertex) || 1;
                dependency.set(
                    predecessor,
                    (dependency.get(predecessor) || 0)
                        + ((paths.get(predecessor) || 0) / vertexPaths) * (1 + (dependency.get(vertex) || 0)),
                );
            }
            if (vertex !== source) {
                centrality.set(vertex, (centrality.get(vertex) || 0) + (dependency.get(vertex) || 0));
            }
        }
    }

    const max = Math.max(...centrality.values(), 1);
    return nodes.map(node => ({ nodeId: node, score: (centrality.get(node) || 0) / max }));
}

export function calculateCommunities(elements: CytoscapeElement[]): GraphScore[] {
    const { nodes, undirected } = buildIndex(elements);
    const labels = new Map(nodes.map((node, index) => [node, index]));

    for (let iteration = 0; iteration < 50; iteration += 1) {
        let changed = false;
        for (const node of nodes) {
            const counts = new Map<number, number>();
            for (const neighbour of undirected.get(node) || []) {
                const label = labels.get(neighbour)!;
                counts.set(label, (counts.get(label) || 0) + 1);
            }
            if (!counts.size) continue;
            const best = [...counts.entries()]
                .sort(([labelA, countA], [labelB, countB]) => countB - countA || labelA - labelB)[0][0];
            if (best !== labels.get(node)) {
                labels.set(node, best);
                changed = true;
            }
        }
        if (!changed) break;
    }

    const canonical = new Map<number, number>();
    const uniqueLabels = [...new Set(labels.values())].sort((a, b) => a - b);
    uniqueLabels.forEach((label, index) => canonical.set(label, index));
    return nodes.map(node => ({
        nodeId: node,
        score: 1,
        clusterId: canonical.get(labels.get(node)!) || 0,
    }));
}
